/**
 * A card as one file, "<title> - FoldMyCard.foldmycard": how a card moves to
 * another device, or is kept safe, now that cards and pictures live only on
 * the device they were made on (2026-10-10). The person moves the file
 * themselves - AirDrop, email, their own cloud drive; it never passes through
 * our server.
 *
 * Inside it is a plain ZIP (stored, not compressed - the pictures are JPEGs
 * already): card.json with the card, every version and the pictures' details,
 * and pictures/<id>/<kind>.jpg for every picture the card shows, cartoon looks
 * included. A card keeps its id wherever it goes: opening a card file adds the
 * card, or updates it when the file's copy is newer than the one here; pictures
 * already on this device (same id) are kept as they are.
 *
 * The same files are what the card folder holds (folder.ts), and a backup of
 * every card is a ZIP of them (saveBackupFile), opened the same way.
 *
 * Since 2026-10-24 a card that invites to an event carries the event too (card.json "events"):
 * its details and guests' names, which are kept nowhere else but on the device (events.ts).
 */
import type { ProjectDocument } from '@foldmycard/scene';
import {
  cardDates, FILE_KINDS, putWholeCard, wholeCard,
  type FileKind, type PictureRow, type ProjectRow, type VersionRow, type WholeCard,
} from './store.ts';
import { getLocalEvent, readLocalEvent, takeLocalEvent, type LocalEvent } from './events.ts';

export const CARD_FORMAT = 'foldmycard/card@1';
export const CARD_EXTENSION = 'foldmycard';

/* ------------------------------------------------------------ a tiny ZIP */

const CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(b: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < b.length; i++) c = CRC[(c ^ b[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** A ZIP of the given files, stored as they are. */
export async function zip(files: { name: string; data: Blob | Uint8Array }[]): Promise<Blob> {
  const enc = new TextEncoder();
  const parts: BlobPart[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const data = f.data instanceof Uint8Array ? f.data : new Uint8Array(await f.data.arrayBuffer());
    const name = enc.encode(f.name);
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true);                 // names are UTF-8
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    parts.push(local.buffer, name, data as BlobPart);
    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);
    cd.setUint16(4, 20, true);
    cd.setUint16(6, 20, true);
    cd.setUint16(8, 0x0800, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, data.length, true);
    cd.setUint32(24, data.length, true);
    cd.setUint16(28, name.length, true);
    cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const cdSize = central.reduce((s, b) => s + b.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, cdSize, true);
  end.setUint32(16, offset, true);
  return new Blob([...parts, ...(central as BlobPart[]), end.buffer], { type: 'application/zip' });
}

/** The files in a stored (uncompressed) ZIP, by name. */
export async function unzip(file: Blob): Promise<Map<string, Blob>> {
  const tailSize = Math.min(file.size, 22 + 65535);
  const tail = new DataView(await file.slice(file.size - tailSize).arrayBuffer());
  let e = -1;
  for (let i = tailSize - 22; i >= 0; i--) if (tail.getUint32(i, true) === 0x06054b50) { e = i; break; }
  if (e < 0) throw new Error('not a ZIP');
  const count = tail.getUint16(e + 10, true), cdSize = tail.getUint32(e + 12, true), cdOffset = tail.getUint32(e + 16, true);
  const cd = new DataView(await file.slice(cdOffset, cdOffset + cdSize).arrayBuffer());
  const dec = new TextDecoder();
  const out = new Map<string, Blob>();
  let p = 0;
  for (let n = 0; n < count; n++) {
    if (cd.getUint32(p, true) !== 0x02014b50) throw new Error('damaged ZIP');
    const method = cd.getUint16(p + 10, true), size = cd.getUint32(p + 20, true);
    const nameLen = cd.getUint16(p + 28, true), extraLen = cd.getUint16(p + 30, true), commentLen = cd.getUint16(p + 32, true);
    const at = cd.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));
    if (method !== 0) throw new Error('compressed ZIP');
    const local = new DataView(await file.slice(at, at + 30).arrayBuffer());
    const start = at + 30 + local.getUint16(26, true) + local.getUint16(28, true);
    out.set(name, file.slice(start, start + size));
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

/* ------------------------------------------------------------ card files */

interface CardJson {
  format: typeof CARD_FORMAT;
  savedAt: string;
  project: ProjectRow;
  versions: VersionRow[];
  pictures: PictureRow[];
  /** The events the card invites to (2026-10-24). */
  events?: LocalEvent[];
}

/** The events a card's versions invite to, as this device has them. */
async function eventsOf(versions: VersionRow[]): Promise<LocalEvent[]> {
  const ids = new Set(versions.map((v) => v.document.invitation?.eventId).filter((x): x is string => !!x));
  const out: LocalEvent[] = [];
  for (const id of ids) {
    const ev = await getLocalEvent(id).catch(() => undefined);
    if (ev) out.push(ev);
  }
  return out;
}

/** The file name a card is saved under: its title, and the start of its id so two cards never share one. */
export const cardFileName = (title: string, id: string) =>
  `${title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '').trim().slice(0, 60) || 'Card'} (${id.slice(0, 8)}).${CARD_EXTENSION}`;

/** A card (every version, and the pictures it shows) as a .foldmycard file. */
export async function saveCardFile(projectId: string): Promise<{ blob: Blob; title: string; name: string; updatedAt: string }> {
  const card = await wholeCard(projectId);
  const json: CardJson = { format: CARD_FORMAT, savedAt: new Date().toISOString(), project: card.project,
    versions: card.versions, pictures: card.pictures.map((p) => p.row), events: await eventsOf(card.versions) };
  const files: { name: string; data: Blob | Uint8Array }[] = [
    { name: 'card.json', data: new TextEncoder().encode(JSON.stringify(json)) },
  ];
  for (const p of card.pictures) for (const f of p.files) files.push({ name: `pictures/${p.row.id}/${f.kind}.jpg`, data: f.blob });
  return { blob: await zip(files), title: card.project.title, name: cardFileName(card.project.title, card.project.id),
    updatedAt: card.project.updatedAt };
}

/**
 * Every card on this device in one file: a ZIP of their card files. Opened
 * like a card file ("Open a card file"), it puts back every card in it.
 */
export async function saveBackupFile(): Promise<{ blob: Blob; name: string; cards: number }> {
  const ids = [...(await cardDates()).keys()];
  const files: { name: string; data: Blob }[] = [];
  for (const id of ids) {
    const c = await saveCardFile(id);
    files.push({ name: c.name, data: c.blob });
  }
  const day = new Date().toISOString().slice(0, 10);
  return { blob: await zip(files), name: `FoldMyCard backup ${day}.${CARD_EXTENSION}`, cards: ids.length };
}

export class CardFileError extends Error {}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isSize = (s: unknown) => !!s && typeof s === 'object' &&
  Number.isFinite((s as { width: unknown }).width) && Number.isFinite((s as { height: unknown }).height);
const damaged = () => new CardFileError('This card file is damaged.');
const iso = (v: unknown, fallback: string) => (typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : fallback);

/**
 * The card in a card file, checked, with the ids it was saved with - a card
 * keeps its id on every device, so opening a newer copy updates it rather
 * than adding a second one.
 */
export async function readCardFile(file: Blob): Promise<WholeCard & { events: LocalEvent[] }> {
  let entries: Map<string, Blob>;
  let json: CardJson;
  try {
    entries = await unzip(file);
    json = JSON.parse(await entries.get('card.json')!.text());
  } catch {
    throw new CardFileError('That is not a FoldMyCard card file.');
  }
  if (json?.format !== CARD_FORMAT || !json.project || !Array.isArray(json.versions) || !json.versions.length ||
      !Array.isArray(json.pictures)) {
    throw new CardFileError('That is not a FoldMyCard card file, or it comes from a newer version of FoldMyCard.');
  }
  const p = json.project;
  if (typeof p.id !== 'string' || !UUID.test(p.id) || typeof p.title !== 'string' || typeof p.templateId !== 'string' ||
      typeof p.paper !== 'string') throw damaged();
  const now = new Date().toISOString();

  const pictures: WholeCard['pictures'] = [];
  for (const r of json.pictures) {
    if (!r || typeof r.id !== 'string' || !UUID.test(r.id) || !isSize(r.sizes?.thumb) || !isSize(r.sizes?.preview)) throw damaged();
    const files: { kind: FileKind; blob: Blob }[] = [];
    for (const kind of FILE_KINDS) {
      const b = entries.get(`pictures/${r.id}/${kind}.jpg`);
      if (b) files.push({ kind, blob: new Blob([b], { type: 'image/jpeg' }) });
    }
    if (!files.some((f) => f.kind === 'preview') || !files.some((f) => f.kind === 'thumb')) {
      throw new CardFileError('A picture is missing from this card file.');
    }
    const cartoon = r.cartoon && isSize(r.cartoon.thumb) && isSize(r.cartoon.preview) &&
      files.some((f) => f.kind === 'cartoon-preview') ? { thumb: r.cartoon.thumb, preview: r.cartoon.preview } : null;
    const bw = r.bw && isSize(r.bw.thumb) && isSize(r.bw.preview) &&
      files.some((f) => f.kind === 'bw-preview') ? { thumb: r.bw.thumb, preview: r.bw.preview } : null;
    pictures.push({
      row: { id: r.id, name: typeof r.name === 'string' ? r.name.slice(0, 200) : null, width: Number(r.width) || r.sizes.preview.width,
        height: Number(r.height) || r.sizes.preview.height, createdAt: iso(r.createdAt, now),
        sizes: { thumb: r.sizes.thumb, preview: r.sizes.preview }, cartoon, bw,
        projectId: p.id, ...(typeof r.library === 'string' ? { library: r.library.slice(0, 60) } : {}) },
      files: files.filter((f) => (cartoon || !f.kind.startsWith('cartoon')) && (bw || !f.kind.startsWith('bw'))),
    });
  }
  const known = new Set(pictures.map((x) => x.row.id));
  const versions: VersionRow[] = [];
  for (const v of json.versions) {
    const doc = v?.document as ProjectDocument | undefined;
    if (!v || typeof v.id !== 'string' || !UUID.test(v.id) || !doc || doc.schema !== 'foldmycard/project@1' ||
        !doc.zones || typeof doc.zones !== 'object' || typeof v.templateHash !== 'string') throw damaged();
    // a picture the file does not carry is taken off the card rather than left pointing nowhere
    for (const slot of Object.values(doc.zones)) if (slot?.image && !known.has(slot.image.id)) slot.image = null;
    versions.push({
      id: v.id, projectId: p.id, label: String(v.label || 'Version A').slice(0, 60),
      basedOn: typeof v.basedOn === 'string' && UUID.test(v.basedOn) ? v.basedOn : null,
      revision: Number.isInteger(v.revision) && v.revision > 0 ? v.revision : 1, templateHash: v.templateHash,
      document: doc, createdAt: iso(v.createdAt, now), updatedAt: iso(v.updatedAt, now),
    });
  }
  const events = (Array.isArray(json.events) ? json.events : []).map(readLocalEvent).filter((e): e is LocalEvent => !!e)
    .map((e) => ({ ...e, cardIds: [...new Set([...e.cardIds, p.id])] }));
  return {
    project: { id: p.id, title: p.title.slice(0, 120) || 'Untitled card', templateId: p.templateId, paper: p.paper,
      createdAt: iso(p.createdAt, now), updatedAt: iso(p.updatedAt, now) },
    versions, pictures, events,
  };
}

/** Only the card's id and when it last changed - read from the file without its pictures. */
export async function peekCardFile(file: Blob): Promise<{ id: string; updatedAt: string } | null> {
  try {
    const json = JSON.parse(await (await unzip(file)).get('card.json')!.text()) as CardJson;
    return json?.format === CARD_FORMAT && UUID.test(json.project?.id) ? { id: json.project.id, updatedAt: String(json.project.updatedAt) } : null;
  } catch {
    return null;
  }
}

export type KnowsTemplate = (id: string, paper: string, hash: string) => Promise<boolean>;

/** Put a card on this device unless it already has the same card, as new or newer. Its events are merged either way. */
export async function takeCard(card: WholeCard & { events?: LocalEvent[] }, knowsTemplate: KnowsTemplate): Promise<'added' | 'updated' | 'kept'> {
  for (const ev of card.events ?? []) await takeLocalEvent(ev);
  const mine = (await cardDates()).get(card.project.id);
  if (mine && mine >= card.project.updatedAt) return 'kept';
  for (const hash of new Set(card.versions.map((v) => v.templateHash))) {
    if (!(await knowsTemplate(card.project.templateId, card.project.paper, hash))) {
      throw new CardFileError(`“${card.project.title}” uses a card design this site does not have.`);
    }
  }
  await putWholeCard(card);
  return mine ? 'updated' : 'added';
}

/**
 * Open a .foldmycard file - one card, or a backup of many. Each card is
 * added, or updated when the file's copy is newer than this device's.
 */
export async function openCardFile(file: Blob, knowsTemplate: KnowsTemplate) {
  let inner: Blob[] = [file];
  try {
    const entries = await unzip(file);
    if (!entries.has('card.json')) {
      inner = [...entries].filter(([n]) => n.endsWith(`.${CARD_EXTENSION}`)).map(([, b]) => b);
      if (!inner.length) throw new CardFileError('That is not a FoldMyCard card file.');
    }
  } catch (err) {
    if (err instanceof CardFileError) throw err;
    throw new CardFileError('That is not a FoldMyCard card file.');
  }
  const done = { added: [] as string[], updated: [] as string[], kept: [] as string[] };
  for (const b of inner) {
    const card = await readCardFile(b);
    done[await takeCard(card, knowsTemplate)].push(card.project.id);
  }
  return { ...done, backup: inner.length > 1 || inner[0] !== file };
}
